import React, { useEffect, useState, useCallback, useMemo } from "react";
import { parsedUser } from "../../common/GetCurrentUser";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import API from "../../common/API";
import moment from "moment";
import { FormatMoney } from "../../common/helpers";
import { Container, Row, Col } from "react-bootstrap";

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

interface ShowcaseSlide {
    id: string;
    title: string;
    type: "line" | "bar" | "doughnut" | "kpi";
    data: any;
    options?: object;
    kpiValue?: string;
    kpiLabel?: string;
    kpiIcon?: string;
}

const ShowcaseDashboard: React.FC = () => {
    const [isDarkMode, setIsDarkMode] = useState<boolean>(true);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [isAutoRotating, setIsAutoRotating] = useState<boolean>(true);
    const [rotationInterval, setRotationInterval] = useState<number>(30);

    // Data states
    const [purchaseMonthly, setPurchaseMonthly] = useState<any[]>([]);
    const [saleMonthly, setSaleMonthly] = useState<any[]>([]);
    const [saleYearly, setSaleYearly] = useState<any[]>([]);
    const [purchaseYearly, setPurchaseYearly] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const user = parsedUser();
    const from = moment().add(-12, "months").format("YYYY-MM-DD");
    const to = moment().format("YYYY-MM-DD");
    const year = moment().format("YYYY");
    // Fetch all data
    const fetchAllData = useCallback(async () => {
        if (!user?.OrganizationId) {
            console.log("❌ fetchAllData: No OrganizationId");
            setLoading(false);
            return;
        }
        console.log("🔄 fetchAllData started for OrganizationId:", user.OrganizationId);
        setLoading(true);




        console.log("📅 Date range:", { from, to, year });

        try {
            // Fetch all data in parallel with proper error handling
            console.log("📡 Fetching Purchase Monthly from: tank/TotalPurchaseMonthly");
            const purchaseMonthlyRes = await API.getAction(
                `tank/TotalPurchaseMonthly?organizationId=${user.OrganizationId}&from=${from}&to=${to}`
            ).catch((err) => {
                console.error("❌ Error fetching Purchase Monthly:", err);
                return { data: { response: [] } };
            });
            const pmCount = Array.isArray(purchaseMonthlyRes.data?.response) ? purchaseMonthlyRes.data.response.length : 0;
            console.log(`✅ Purchase Monthly returned: ${pmCount} records`);

            console.log("📡 Fetching Sale Monthly from: tank/TotalSaleMonthly");
            const saleMonthlyRes = await API.getAction(
                `tank/TotalSaleMonthly?organizationId=${user.OrganizationId}&from=${from}&to=${to}`
            ).catch((err) => {
                console.error("❌ Error fetching Sale Monthly:", err);
                return { data: { response: [] } };
            });
            const smCount = Array.isArray(saleMonthlyRes.data?.response) ? saleMonthlyRes.data.response.length : 0;
            console.log(`✅ Sale Monthly returned: ${smCount} records`);

            console.log("📡 Fetching Sale Yearly from: tank/TotalSaleYearly");
            const saleYearlyRes = await API.getAction(
                `tank/TotalSaleYearly?organizationId=${user.OrganizationId}&year=${year}`
            ).catch((err) => {
                console.error("❌ Error fetching Sale Yearly:", err);
                return { data: { response: [] } };
            });
            const syCount = Array.isArray(saleYearlyRes.data?.response) ? saleYearlyRes.data.response.length : 0;
            console.log(`✅ Sale Yearly returned: ${syCount} records`);

            console.log("📡 Fetching Purchase Yearly from: tank/TotalPurchaseYearly");
            const purchaseYearlyRes = await API.getAction(
                `tank/TotalPurchaseYearly?organizationId=${user.OrganizationId}&year=${year}`
            ).catch((err) => {
                console.error("❌ Error fetching Purchase Yearly:", err);
                return { data: { response: [] } };
            });
            const pyCount = Array.isArray(purchaseYearlyRes.data?.response) ? purchaseYearlyRes.data.response.length : 0;
            console.log(`✅ Purchase Yearly returned: ${pyCount} records`);

            console.log("📊 Summary of all fetches:", {
                purchaseMonthly: pmCount,
                saleMonthly: smCount,
                saleYearly: syCount,
                purchaseYearly: pyCount,
                totalRecords: pmCount + smCount + syCount + pyCount
            });

            setPurchaseMonthly(Array.isArray(purchaseMonthlyRes.data?.response) ? purchaseMonthlyRes.data.response : []);
            setSaleMonthly(Array.isArray(saleMonthlyRes.data?.response) ? saleMonthlyRes.data.response : []);
            setSaleYearly(Array.isArray(saleYearlyRes.data?.response) ? saleYearlyRes.data.response : []);
            setPurchaseYearly(Array.isArray(purchaseYearlyRes.data?.response) ? purchaseYearlyRes.data.response : []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    }, [user?.OrganizationId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (loading) {
                setLoading(false);
            }
        }, 5000); // Force loading to false after 5 seconds

        return () => clearTimeout(timer);
    }, [loading]);

    useEffect(() => {
        if (user?.OrganizationId) {
            console.log("🔍 ShowcaseDashboard mounted with OrganizationId:", user.OrganizationId);
            fetchAllData();
        } else {
            console.log("❌ No OrganizationId found. User:", user);
            setLoading(false);
        }
    }, [user?.OrganizationId, fetchAllData]);

    // Theme colors
    const themeColors = useMemo(
        () => ({
            background: isDarkMode ? "#1a1a2e" : "#f8f9fa",
            cardBackground: isDarkMode ? "#16213e" : "#ffffff",
            text: isDarkMode ? "#ffffff" : "#1a1a2e",
            secondaryText: isDarkMode ? "#a0a0a0" : "#6c757d",
            accent: "#4a69bd",
            success: "#10B981",
            warning: "#F59E0B",
            danger: "#EF4444",
            info: "#3B82F6",
            chartColors: isDarkMode
                ? ["#4a69bd", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"]
                : ["#2d4059", "#28a745", "#ffc107", "#dc3545", "#6f42c1"],
        }),
        [isDarkMode]
    );

    // Chart options
    const getChartOptions = useCallback(
        (title: string) => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: "top" as const,
                    labels: {
                        color: themeColors.text,
                        font: { size: 14, weight: "bold" as const },
                    },
                },
                title: {
                    display: true,
                    text: title,
                    color: themeColors.text,
                    font: { size: 24, weight: "bold" as const },
                },
            },
            scales: {
                x: {
                    ticks: { color: themeColors.secondaryText, font: { size: 12 } },
                    grid: { color: isDarkMode ? "#333" : "#e0e0e0" },
                },
                y: {
                    ticks: { color: themeColors.secondaryText, font: { size: 12 } },
                    grid: { color: isDarkMode ? "#333" : "#e0e0e0" },
                },
            },
        }),
        [themeColors, isDarkMode]
    );

    // Build slides
    const slides: ShowcaseSlide[] = useMemo(() => {
        const slideList: ShowcaseSlide[] = [];

        // ============ GRÁFICA 1: Compras y Entregas Mensuales Juntas ============
        if (purchaseMonthly.length > 0 || saleMonthly.length > 0) {
            const dateMap = new Map<string, any>();

            purchaseMonthly.forEach((item: any) => {
                const dateKey = moment(item.date).format("YYYY-MM");
                if (!dateMap.has(dateKey)) {
                    dateMap.set(dateKey, {
                        date: dateKey,
                        displayDate: moment(item.date).format("MMM YYYY"),
                        purchase: 0,
                        sale: 0,
                    });
                }
                dateMap.get(dateKey)!.purchase = item.purchase || 0;
            });

            saleMonthly.forEach((item: any) => {
                const dateKey = moment(item.date).format("YYYY-MM");
                if (!dateMap.has(dateKey)) {
                    dateMap.set(dateKey, {
                        date: dateKey,
                        displayDate: moment(item.date).format("MMM YYYY"),
                        purchase: 0,
                        sale: 0,
                    });
                }
                dateMap.get(dateKey)!.sale = item.total || 0;
            });

            const mergedData = Array.from(dateMap.values()).sort(
                (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
            );

            slideList.push({
                id: "purchase-sale-monthly",
                title: `Compras vs Entregas Mensuales (${from} - ${to})`,
                type: "bar",
                data: {
                    labels: mergedData.map((item: any) => item.displayDate),
                    datasets: [
                        {
                            label: "Galones Comprados",
                            data: mergedData.map((item: any) => item.purchase),
                            borderColor: themeColors.chartColors[0],
                            backgroundColor: themeColors.chartColors[0] + "33",
                            borderWidth: 3,
                            fill: true,
                            tension: 0.4,
                        },
                        {
                            label: "Galones Entregados",
                            data: mergedData.map((item: any) => item.sale),
                            borderColor: themeColors.chartColors[1],
                            backgroundColor: themeColors.chartColors[1] + "33",
                            borderWidth: 3,
                            fill: true,
                            tension: 0.4,
                        },
                    ],
                },
                options: getChartOptions(`Compras vs Entregas Mensuales (${from} - ${to})`,),
            });
        }

        // ============ GRÁFICA 2: Total de Compras y Ventas Anuales ============
        if (purchaseYearly.length > 0 || saleYearly.length > 0) {
            const totalPurchase = purchaseYearly.reduce(
                (a: number, b: any) => a + (b.purchase || 0),
                0
            );
            const totalSale = saleYearly.reduce((a: number, b: any) => a + (b.total || 0), 0);

            slideList.push({
                id: "totals-annual",
                title: "Totales Anuales",
                type: "bar",
                data: {
                    labels: ["Compras", "Entregas"],
                    datasets: [
                        {
                            label: "Galones",
                            data: [totalPurchase, totalSale],
                            backgroundColor: [themeColors.chartColors[0], themeColors.chartColors[1]],
                            borderColor: [themeColors.chartColors[0], themeColors.chartColors[1]],
                            borderWidth: 2,
                        },
                    ],
                },
                options: getChartOptions(`Acumulado Sept. 2019 - ${year} a la fecha - Compras: ${FormatMoney(totalPurchase)} gl | Entregas: ${FormatMoney(totalSale)} gl`),
            });
        }

        // ============ GRÁFICA 3: Granel vs Detalle ============
        if (saleYearly.length > 0) {
            const totalGranel = saleYearly.reduce((a: number, b: any) => a + (b.granelTotal || 0), 0);
            const totalDetails = saleYearly.reduce((a: number, b: any) => a + (b.detailsTotal || 0), 0);

            slideList.push({
                id: "granel-detail",
                title: "Distribución de Entregas",
                type: "doughnut",
                data: {
                    labels: ["Granel", "Detalle"],
                    datasets: [
                        {
                            data: [totalGranel, totalDetails],
                            backgroundColor: [
                                themeColors.chartColors[0],
                                themeColors.chartColors[1],
                            ],
                            borderColor: isDarkMode ? "#1a1a2e" : "#ffffff",
                            borderWidth: 4,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: "bottom" as const,
                            labels: {
                                color: themeColors.text,
                                font: { size: 16, weight: "bold" as const },
                                padding: 20,
                            },
                        },
                        title: {
                            display: true,
                            text: `Acumulado Sept. 2019 - ${year} a la fecha por Tipo venta > Granel: ${FormatMoney(totalGranel)} gl | Detalle: ${FormatMoney(totalDetails)} gl`,
                            color: themeColors.text,
                            font: { size: 18, weight: "bold" as const },
                        },
                    },
                },
            });
        }

        console.log("Total slides created:", slideList.length);
        return slideList;
    }, [
        purchaseMonthly,
        saleMonthly,
        saleYearly,
        purchaseYearly,
        themeColors,
        getChartOptions,
        isDarkMode,
    ]);

    // Auto-rotation
    useEffect(() => {
        if (!isAutoRotating || slides.length === 0) return;

        const interval = setInterval(() => {
            setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
        }, rotationInterval * 1000);

        return () => clearInterval(interval);
    }, [isAutoRotating, slides.length, rotationInterval]);

    // Fullscreen toggle
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    // Navigation
    const goToSlide = (index: number) => {
        setCurrentSlideIndex(index);
        setIsAutoRotating(false);
    };

    const nextSlide = () => {
        setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
    };

    const prevSlide = () => {
        setCurrentSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);
    };

    // KPI Summary component
    const totalPurchaseYearly = purchaseYearly.reduce(
        (a: number, b: any) => a + (b.purchase || 0),
        0
    );
    const totalSaleYearly = saleYearly.reduce(
        (a: number, b: any) => a + (b.total || 0),
        0
    );
    const totalGranel = saleYearly.reduce(
        (a: number, b: any) => a + (b.granelTotal || 0),
        0
    );
    const totalDetails = saleYearly.reduce(
        (a: number, b: any) => a + (b.detailsTotal || 0),
        0
    );
    const totalPurchaseMonthly = purchaseMonthly.reduce(
        (a: number, b: any) => a + (b.purchase || 0),
        0
    );
    const totalSaleMonthly = saleMonthly.reduce(
        (a: number, b: any) => a + (b.total || 0),
        0
    );

    const renderKPISummary = () => (
        <div className="h-100 d-flex flex-column justify-content-center p-4">
            <h1
                className="text-center mb-5"
                style={{ color: themeColors.text, fontSize: "3rem" }}
            >
                Indicadores Clave del Año {moment().format("YYYY")}
            </h1>
            <Row className="g-4">
                <Col xs={12} md={6} lg={3}>
                    <div
                        className="p-4 rounded-4 text-center h-100"
                        style={{
                            backgroundColor: themeColors.cardBackground,
                            border: `2px solid ${themeColors.accent}`,
                        }}
                    >
                        <i
                            className="fas fa-shopping-cart mb-3"
                            style={{ fontSize: "3rem", color: themeColors.accent }}
                        ></i>
                        <h2 style={{ color: themeColors.text, fontSize: "2.5rem" }}>
                            {FormatMoney(totalPurchaseYearly)}
                        </h2>
                        <p
                            style={{
                                color: themeColors.secondaryText,
                                fontSize: "1.2rem",
                                marginBottom: 0,
                            }}
                        >
                            Galones Comprados (Anual)
                        </p>
                    </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <div
                        className="p-4 rounded-4 text-center h-100"
                        style={{
                            backgroundColor: themeColors.cardBackground,
                            border: `2px solid ${themeColors.success}`,
                        }}
                    >
                        <i
                            className="fas fa-truck mb-3"
                            style={{ fontSize: "3rem", color: themeColors.success }}
                        ></i>
                        <h2 style={{ color: themeColors.text, fontSize: "2.5rem" }}>
                            {FormatMoney(totalSaleYearly)}
                        </h2>
                        <p
                            style={{
                                color: themeColors.secondaryText,
                                fontSize: "1.2rem",
                                marginBottom: 0,
                            }}
                        >
                            Galones Entregados (Anual)
                        </p>
                    </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <div
                        className="p-4 rounded-4 text-center h-100"
                        style={{
                            backgroundColor: themeColors.cardBackground,
                            border: `2px solid ${themeColors.warning}`,
                        }}
                    >
                        <i
                            className="fas fa-industry mb-3"
                            style={{ fontSize: "3rem", color: themeColors.warning }}
                        ></i>
                        <h2 style={{ color: themeColors.text, fontSize: "2.5rem" }}>
                            {FormatMoney(totalGranel)}
                        </h2>
                        <p
                            style={{
                                color: themeColors.secondaryText,
                                fontSize: "1.2rem",
                                marginBottom: 0,
                            }}
                        >
                            Granel (Anual)
                        </p>
                    </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <div
                        className="p-4 rounded-4 text-center h-100"
                        style={{
                            backgroundColor: themeColors.cardBackground,
                            border: `2px solid ${themeColors.info}`,
                        }}
                    >
                        <i
                            className="fas fa-boxes mb-3"
                            style={{ fontSize: "3rem", color: themeColors.info }}
                        ></i>
                        <h2 style={{ color: themeColors.text, fontSize: "2.5rem" }}>
                            {FormatMoney(totalDetails)}
                        </h2>
                        <p
                            style={{
                                color: themeColors.secondaryText,
                                fontSize: "1.2rem",
                                marginBottom: 0,
                            }}
                        >
                            Detalle (Anual)
                        </p>
                    </div>
                </Col>
            </Row>
            <Row className="g-4 mt-3">
                <Col xs={12} md={6}>
                    <div
                        className="p-4 rounded-4 text-center"
                        style={{
                            backgroundColor: themeColors.cardBackground,
                            border: `2px solid ${themeColors.danger}`,
                        }}
                    >
                        <i
                            className="fas fa-calendar-alt mb-3"
                            style={{ fontSize: "2.5rem", color: themeColors.danger }}
                        ></i>
                        <h3 style={{ color: themeColors.text, fontSize: "2rem" }}>
                            {FormatMoney(totalPurchaseMonthly)} gl
                        </h3>
                        <p
                            style={{
                                color: themeColors.secondaryText,
                                fontSize: "1rem",
                                marginBottom: 0,
                            }}
                        >
                            Compras Últimos 12 Meses
                        </p>
                    </div>
                </Col>
                <Col xs={12} md={6}>
                    <div
                        className="p-4 rounded-4 text-center"
                        style={{
                            backgroundColor: themeColors.cardBackground,
                            border: `2px solid ${themeColors.success}`,
                        }}
                    >
                        <i
                            className="fas fa-chart-line mb-3"
                            style={{ fontSize: "2.5rem", color: themeColors.success }}
                        ></i>
                        <h3 style={{ color: themeColors.text, fontSize: "2rem" }}>
                            {FormatMoney(totalSaleMonthly)} gl
                        </h3>
                        <p
                            style={{
                                color: themeColors.secondaryText,
                                fontSize: "1rem",
                                marginBottom: 0,
                            }}
                        >
                            Entregas Últimos 12 Meses
                        </p>
                    </div>
                </Col>
            </Row>
        </div>
    );

    const renderChart = (slide: ShowcaseSlide) => {
        if (slide.type === "kpi") return renderKPISummary();

        const ChartComponent = {
            line: Line,
            bar: Bar,
            doughnut: Doughnut,
        }[slide.type];

        return (
            <div className="h-100 p-4">
                <ChartComponent data={slide.data} options={slide.options as any} />
            </div>
        );
    };

    if (loading && slides.length === 0) {
        return (
            <div
                className="d-flex justify-content-center align-items-center vh-100"
                style={{ backgroundColor: themeColors.background }}
            >
                <div className="text-center">
                    <div
                        className="spinner-border mb-3"
                        style={{ width: "4rem", height: "4rem", color: themeColors.accent }}
                    ></div>
                    <h3 style={{ color: themeColors.text }}>Cargando Dashboard...</h3>
                    <p style={{ color: themeColors.secondaryText }}>
                        OrganizationId: {user?.OrganizationId || "No encontrado"}
                    </p>
                </div>
            </div>
        );
    }
    // NO DATA
    if (slides.length === 0) {
        return (
            <div
                className="d-flex justify-content-center align-items-center vh-100"
                style={{ backgroundColor: themeColors.background }}
            >
                <div className="text-center p-4">
                    <i
                        className="fas fa-exclamation-circle mb-3"
                        style={{ fontSize: "4rem", color: themeColors.accent }}
                    ></i>
                    <h3 style={{ color: themeColors.text }}>Sin datos disponibles</h3>
                    <p style={{ color: themeColors.secondaryText }} className="mb-3">
                        No se pudieron cargar los datos del dashboard
                    </p>
                    <div style={{ backgroundColor: themeColors.cardBackground, padding: "1rem", borderRadius: "8px", marginTop: "1rem" }}>
                        <small style={{ color: themeColors.secondaryText }}>
                            <div>📊 Compras Mensuales: {purchaseMonthly.length} registros</div>
                            <div>📦 Entregas Mensuales: {saleMonthly.length} registros</div>
                            <div>📈 Entregas Anuales: {saleYearly.length} registros</div>
                            <div>🛒 Compras Anuales: {purchaseYearly.length} registros</div>
                            <div style={{ marginTop: "0.5rem", color: themeColors.accent }}>
                                OrganizationId: {user?.OrganizationId || "No encontrado"}
                            </div>
                        </small>
                    </div>
                    <button
                        onClick={() => {
                            setLoading(true);
                            fetchAllData();
                        }}
                        className="btn btn-primary mt-3"
                    >
                        <i className="fas fa-sync-alt me-2"></i> Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="showcase-dashboard"
            style={{
                backgroundColor: themeColors.background,
                minHeight: "100vh",
                transition: "all 0.3s ease",
            }}
        >
            {/* Control Bar */}
            <div
                className="d-flex justify-content-between align-items-center px-4 py-2"
                style={{
                    backgroundColor: themeColors.cardBackground,
                    borderBottom: `1px solid ${isDarkMode ? "#333" : "#dee2e6"}`,
                }}
            >
                <div className="d-flex align-items-center gap-3">
                    <h5 className="m-0" style={{ color: themeColors.text }}>
                        <i className="fas fa-chart-pie me-2"></i>
                        Zaboka
                    </h5>
                    <span style={{ color: themeColors.secondaryText }}>
                        {currentSlideIndex + 1} / {slides.length}
                    </span>
                </div>

                <div className="d-flex align-items-center gap-3">
                    {/* Slide Indicators */}
                    <div className="d-flex gap-2">
                        {slides.map((slide, index) => (
                            <button
                                key={slide.id}
                                onClick={() => goToSlide(index)}
                                className="btn btn-sm rounded-circle p-0"
                                style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor:
                                        index === currentSlideIndex
                                            ? themeColors.accent
                                            : themeColors.secondaryText,
                                    border: "none",
                                    transition: "all 0.2s",
                                }}
                            />
                        ))}
                    </div>

                    {/* Rotation Interval */}
                    <select
                        value={rotationInterval}
                        onChange={(e) => setRotationInterval(Number(e.target.value))}
                        className="form-select form-select-sm"
                        style={{
                            width: "auto",
                            backgroundColor: themeColors.cardBackground,
                            color: themeColors.text,
                            border: `1px solid ${isDarkMode ? "#444" : "#ced4da"}`,
                        }}
                    >
                        <option value={10}>10s</option>
                        <option value={15}>15s</option>
                        <option value={30}>30s</option>
                        <option value={60}>60s</option>
                    </select>

                    {/* Auto Rotate Toggle */}
                    <button
                        onClick={() => setIsAutoRotating(!isAutoRotating)}
                        className={`btn btn-sm ${isAutoRotating ? "btn-success" : "btn-outline-secondary"
                            }`}
                        title={isAutoRotating ? "Pausar rotación" : "Iniciar rotación"}
                    >
                        <i className={`fas fa-${isAutoRotating ? "pause" : "play"}`}></i>
                    </button>

                    {/* Theme Toggle */}
                    <button
                        onClick={() => setIsDarkMode(!isDarkMode)}
                        className="btn btn-sm btn-outline-secondary"
                        title={isDarkMode ? "Modo Claro" : "Modo Oscuro"}
                    >
                        <i className={`fas fa-${isDarkMode ? "sun" : "moon"}`}></i>
                    </button>

                    {/* Fullscreen Toggle */}
                    <button
                        onClick={toggleFullscreen}
                        className="btn btn-sm btn-outline-primary"
                        title={isFullscreen ? "Salir Pantalla Completa" : "Pantalla Completa"}
                    >
                        <i
                            className={`fas fa-${isFullscreen ? "compress" : "expand"}`}
                        ></i>
                    </button>

                    {/* Navigation */}
                    <div className="btn-group">
                        <button
                            onClick={prevSlide}
                            className="btn btn-sm btn-outline-primary"
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <button
                            onClick={nextSlide}
                            className="btn btn-sm btn-outline-primary"
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <Container
                fluid
                className="p-0"
                style={{ height: "calc(100vh - 60px)" }}
            >
                {slides.length > 0 && slides[currentSlideIndex] && (
                    <div className="h-100 p-3">
                        <div
                            className="h-100 rounded-4"
                            style={{
                                backgroundColor: themeColors.cardBackground,
                                boxShadow: isDarkMode
                                    ? "0 8px 32px rgba(0,0,0,0.3)"
                                    : "0 8px 32px rgba(0,0,0,0.1)",
                            }}
                        >
                            {renderChart(slides[currentSlideIndex])}
                        </div>
                    </div>
                )}
            </Container>

            {/* Footer */}
            <div
                className="position-fixed bottom-0 start-0 end-0 px-4 py-2 text-center"
                style={{
                    backgroundColor: themeColors.cardBackground,
                    borderTop: `1px solid ${isDarkMode ? "#333" : "#dee2e6"}`,
                }}
            >
                <small style={{ color: themeColors.secondaryText }}>
                    Última actualización: {moment().format("DD/MM/YYYY HH:mm")} |{" "}
                    {slides[currentSlideIndex]?.title || ""}
                </small>
            </div>
        </div>
    );
};

export default ShowcaseDashboard;
